import {ProjectTimeStatus, UserSumsProjectTime} from "@/shared/timr";
import {EMPTY_CALLBACK} from "@/shared/utils/misc";
import {calculateDurationSince, formatDuration} from "@/shared/utils/datefunctions";
import {RunningTimeStatus, UserSumsChartRange} from "@/shared/timrTypes";

declare const c3: typeof import("c3");
declare const d3: typeof import("d3");

export interface UserSumsProjectTimeChart {
	durationBillable: number;
	durationNotBillable: number;
	duration: number;
	popoverContentElement: JQuery;
}

export class ProjectTimeStatistics {

	private container: JQuery;
	private projectTimeUserSums: UserSumsProjectTime;
	private todayChart: c3.ChartAPI;
	private thisWeekChart: c3.ChartAPI;
	private thisMonthChart: c3.ChartAPI;

	constructor(container: JQuery) {
		this.container = container;
		this.init();
	}

	/**
	 * Updates the project time statistics based on the provided project time status.
	 * If there is a running project time, it calculates the duration since the start
	 * and updates the user sums accordingly.
	 *
	 * @param projectTimeStatus The current status of the project time, including user sums and running time.
	 */
	public updateStatistics(projectTimeStatus: ProjectTimeStatus) {
		const {projectTimeUserSums, runningProjectTime} = projectTimeStatus;
		if (projectTimeUserSums) {
			const projectTimeUserSumsCopy = {...projectTimeStatus.projectTimeUserSums};
			if (runningProjectTime) {
				// if runningProjectTime is paused the breakTime isn't updated yet
				const runningDurationMs = calculateDurationSince(
					runningProjectTime.start,
					runningProjectTime.breakTime,
					runningProjectTime.status === RunningTimeStatus.PAUSED ? runningProjectTime.pausedAt : null
				).total('milliseconds');
				projectTimeUserSumsCopy.durationDay += runningDurationMs;
				projectTimeUserSumsCopy.durationWeek += runningDurationMs;
				projectTimeUserSumsCopy.durationMonth += runningDurationMs;
				if (runningProjectTime.billable) {
					projectTimeUserSumsCopy.durationBillableDay += runningDurationMs;
					projectTimeUserSumsCopy.durationBillableWeek += runningDurationMs;
					projectTimeUserSumsCopy.durationBillableMonth += runningDurationMs;
				} else {
					projectTimeUserSumsCopy.durationNotBillableDay += runningDurationMs;
					projectTimeUserSumsCopy.durationNotBillableWeek += runningDurationMs;
					projectTimeUserSumsCopy.durationNotBillableMonth += runningDurationMs;
				}
			}
			this.updateValues(projectTimeUserSumsCopy);
		}
	}

	private updateValues(projectTimeUserSums: UserSumsProjectTime, forcePopoverRebuild?: UserSumsChartRange) {
		this.projectTimeUserSums = projectTimeUserSums;

		this.handleChartPopoverBuild(UserSumsChartRange.Day, forcePopoverRebuild === UserSumsChartRange.Day);
		this.handleChartPopoverBuild(UserSumsChartRange.Week, forcePopoverRebuild === UserSumsChartRange.Week);
		this.handleChartPopoverBuild(UserSumsChartRange.Month, forcePopoverRebuild === UserSumsChartRange.Month);

		this.container.find("#projectTimeDurationToday").text(formatDuration(projectTimeUserSums.durationDay));
		this.container.find("#projectTimeDurationThisWeek").text(formatDuration(projectTimeUserSums.durationWeek));
		this.container.find("#projectTimeDurationThisMonth").text(formatDuration(projectTimeUserSums.durationMonth));

		this.container.find("#projectTimeDurationBillableToday").text(formatDuration(projectTimeUserSums.durationBillableDay));
		this.container.find("#projectTimeDurationBillableThisWeek").text(formatDuration(projectTimeUserSums.durationBillableWeek));
		this.container.find("#projectTimeDurationBillableThisMonth").text(formatDuration(projectTimeUserSums.durationBillableMonth));

		this.todayChart.load({
			columns: this.buildChartColumns(projectTimeUserSums.durationBillableDay, projectTimeUserSums.durationNotBillableDay)
		});
		this.thisWeekChart.load({
			columns: this.buildChartColumns(projectTimeUserSums.durationBillableWeek, projectTimeUserSums.durationNotBillableWeek)
		});
		this.thisMonthChart.load({
			columns: this.buildChartColumns(projectTimeUserSums.durationBillableMonth, projectTimeUserSums.durationNotBillableMonth)
		});
	}

	private handleChartPopoverBuild(chartRange: UserSumsChartRange, forcePopoverRebuild = false) {
		const chartRangeKey = UserSumsChartRange[chartRange];
		const containerSelector = "#projectTimeThis" + chartRangeKey + "ChartPopover";
		const popoverSelector = "this-" + chartRangeKey.toLowerCase() + "-chart-popover";

		if ($(`.${popoverSelector}`).length === 0 || forcePopoverRebuild) {
			this.container.find(containerSelector).popover("destroy").popover({
				animation: false,
				trigger: "hover",
				placement: "top",
				container: ".content-wrapper",
				delay: {show: 400, hide: 0},
				html: true,
				template: `<div class="popover ${popoverSelector} statistics-chart-popover" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>`,
				content: () => this.renderChartPopover(chartRange)
			});
			if (forcePopoverRebuild) {
				this.container.find(containerSelector).popover("show");
			}
		} else {
			this.updateChartPopover(chartRange);
		}
	}

	private init() {
		this.todayChart = this.initChart("#projectTimeTodayChart");
		this.thisWeekChart = this.initChart("#projectTimeThisWeekChart");
		this.thisMonthChart = this.initChart("#projectTimeThisMonthChart");
	}

	private initChart(bindTo: string): c3.ChartAPI {
		return c3.generate({
			bindto: bindTo,
			size: {height: 110},
			interaction: {enabled: false},
			data: {
				// series colours come from CSS (components/charts.less, .c3-arc-<id>), themed there
				names: {
					billable: messages["statisticsPanel.record.billable"],
					notBillable: messages["statisticsPanel.record.notbillable"]
				},
				columns: this.buildChartColumns(0, 0),
				type: "donut",
				order: null
			},
			legend: {
				show: false,
				item: {
					onclick: EMPTY_CALLBACK
				}
			},
			donut: {
				label: {
					show: false
				},
				width: 7
			}
		});
	}

	private buildChartColumns(durationBillable: number, durationNotBillable: number): c3.PrimitiveArray[] {
		return [
			["empty", durationBillable === 0 && durationNotBillable === 0 ? 1 : 0],
			["billable", durationBillable],
			["notBillable", durationNotBillable]
		];
	}

	private getUserSumsProjectTimeChart(chartRange: UserSumsChartRange): UserSumsProjectTimeChart {
		switch (chartRange) {
			case UserSumsChartRange.Day:
				return this.handleCase(this.projectTimeUserSums.durationBillableDay, this.projectTimeUserSums.durationNotBillableDay, this.projectTimeUserSums.durationDay,
					$(".this-day-chart-popover"));
			case UserSumsChartRange.Week:
				return this.handleCase(this.projectTimeUserSums.durationBillableWeek, this.projectTimeUserSums.durationNotBillableWeek, this.projectTimeUserSums.durationWeek,
					$(".this-week-chart-popover"));
			case UserSumsChartRange.Month:
				return this.handleCase(this.projectTimeUserSums.durationBillableMonth, this.projectTimeUserSums.durationNotBillableMonth, this.projectTimeUserSums.durationMonth,
					$(".this-month-chart-popover"));
		}
	}

	private handleCase(durationBillable: number, durationNotBillable: number, duration: number, popoverContentElement: JQuery): UserSumsProjectTimeChart {
		return {
			durationBillable: durationBillable,
			durationNotBillable: durationNotBillable,
			duration: duration,
			popoverContentElement: popoverContentElement
		};
	}

	private updateChartPopover(chartRange: UserSumsChartRange) {
		const userSumsChart = this.getUserSumsProjectTimeChart(chartRange);
		if (!(userSumsChart.durationBillable === 0 && userSumsChart.durationNotBillable === 0) && userSumsChart.popoverContentElement.find(".empty-statistics").length === 0) {
			userSumsChart.popoverContentElement.find(".durationBillable").text(formatDuration(userSumsChart.durationBillable));
			userSumsChart.popoverContentElement.find(".durationBillableSum").text("(" + d3.format(".2%")(userSumsChart.durationBillable / userSumsChart.duration) + ")");
			userSumsChart.popoverContentElement.find(".durationNotBillable").text(formatDuration(userSumsChart.durationNotBillable));
			userSumsChart.popoverContentElement.find(".durationNotBillableSum").text("(" + d3.format(".2%")(userSumsChart.durationNotBillable / userSumsChart.duration) + ")");
			userSumsChart.popoverContentElement.find(".durationSum").text(formatDuration(userSumsChart.duration));
		} else {
			this.updateValues(this.projectTimeUserSums, chartRange);
		}
	}

	private renderChartPopover(chartRange: UserSumsChartRange) {
		const userSumsChart = this.getUserSumsProjectTimeChart(chartRange);
		if (userSumsChart.durationBillable === 0 && userSumsChart.durationNotBillable === 0 && userSumsChart.duration === 0) {
			return `<table class="statistics-chart-popover-table-empty">
							<tr>
								<td class="text-center"><i class="fa fa-circle empty-statistics statistics-chart-popover-circle-empty"></i> ${messages["statisticsPanel.record.empty"]}</td>
							</tr>
						</table>`;
		} else {
			return `<table class="statistics-chart-popover-table">
							<tr>
								<td><i class="fa fa-circle statistics-chart-popover-circle-billable"></i> ${messages["statisticsPanel.record.billable"]}</td>
								<td class="duration-width text-right durationBillable">${formatDuration(userSumsChart.durationBillable)}</td>
								<td class="duration-width text-right durationBillableSum">(${d3.format(".2%")(userSumsChart.durationBillable / userSumsChart.duration)})</td>
							</tr>
							<tr class="statistics-chart-popover-total-line">
								<td><i class="fa fa-circle statistics-chart-popover-circle-not-billable"></i> ${messages["statisticsPanel.record.notbillable"]}</td>
								<td class="duration-width text-right durationNotBillable">${formatDuration(userSumsChart.durationNotBillable)} </td>
								<td class="duration-width text-right durationNotBillableSum">(${d3.format(".2%")(userSumsChart.durationNotBillable / userSumsChart.duration)})</td>
							</tr>
							<tr class="statistics-chart-sum">
								<td><i class="fa fa-circle statistics-chart-popover-circle-transparent"></i> ${messages["statisticsPanel.record.sum"]}</td>
								<td class="duration-width text-right durationSum">${formatDuration(userSumsChart.duration)}</td>
							</tr>
						</table>`;
		}
	}
}
